import type { IsoDate, Measurement } from "./types.js";
import type { PredictionRow } from "../../shared/types.js";
import { SEASON_1 } from "../../shared/plan.js";

const PLAN_END = SEASON_1.end;
const TARGET_END_WEIGHT = SEASON_1.targetWeight;

/**
 * 生日賽季競猜的結算與排名。明確綁 `SEASON_1`：這一季的結果不能隨期程變動。
 *
 * 與 shared/prediction.ts 分工：那邊是「什麼時候還能下注」，前後端都要強制
 * 執行；這裡是「結果怎麼算」，**只有畫面用得到**，後端從不結算。
 *
 * 與 src/domain/ 的鐵則一致：不讀系統時間，「今天」由呼叫端傳入。
 */

export interface SettledBet extends PredictionRow {
  /** 陣營猜對了沒。 */
  correct: boolean;
  /** 與結算體重的差距。沒填公斤數是 null。 */
  diff: number | null;
  /** 名次。答錯或還沒結算是 null。 */
  rank: number | null;
}

export interface Settlement {
  /** 還沒到結算日、或完全沒有體重時是 false。 */
  settled: boolean;
  finalWeight: number | null;
  finalDate: IsoDate | null;
  /** 達標與否。沒結算是 null。 */
  reached: boolean | null;
  reachCamp: SettledBet[];
  missCamp: SettledBet[];
  /** 只有猜對陣營的人在裡面，依誤差排序。 */
  ranking: SettledBet[];
}

/**
 * 結算用的體重：生日當天那一筆。
 *
 * 那天沒量的話往前取計畫期間內最後一筆——不然一次忘了站上體脂計就沒有結果。
 * 計畫期間之後才量的不算，否則結算會隨著之後的量測一直變。
 */
function finalMeasurement(measurements: Measurement[]): Measurement | null {
  const inPlan = measurements
    .filter(row => row.measurement_date <= PLAN_END && Number.isFinite(row.weight_kg))
    .sort((a, b) => a.measurement_date.localeCompare(b.measurement_date));
  return inPlan.at(-1) || null;
}

/**
 * 名次：誤差相同就並列，下一名跳號。
 *
 * 沒填公斤數的誤差都是 null，所以他們彼此也會並列——那是對的：
 * 他們都答對了，只是都沒有可以比的數字。
 */
export function assignRanks<T extends { diff: number | null; rank: number | null }>(rows: T[]): T[] {
  let lastDiff: number | null = null;
  let lastRank = 0;
  return rows.map((row, index) => {
    const same = index > 0 && row.diff === lastDiff;
    const rank = same ? lastRank : index + 1;
    lastDiff = row.diff;
    lastRank = rank;
    return { ...row, rank };
  });
}

export function settlePredictions(
  predictions: PredictionRow[],
  measurements: Measurement[],
  today: IsoDate
): Settlement {
  const final = today >= PLAN_END ? finalMeasurement(measurements) : null;
  const finalWeight = final ? (final.weight_kg as number) : null;
  const reached = finalWeight === null ? null : finalWeight < TARGET_END_WEIGHT;

  const bets: SettledBet[] = predictions.map(prediction => ({
    ...prediction,
    correct: reached !== null && !!prediction.will_reach === reached,
    diff: finalWeight !== null && prediction.weight_kg !== null
      ? Math.abs(prediction.weight_kg - finalWeight)
      : null,
    rank: null
  }));

  /**
   * 排名只收猜對陣營的人。有填公斤數的照誤差排在前面，沒填的照名字排在後面
   * 並列——他答對了，只是沒有可以比的數字。
   */
  const correct = bets.filter(row => row.correct);
  const ranking = reached === null ? [] : assignRanks([
    ...correct.filter(row => row.diff !== null).sort((a, b) => (a.diff as number) - (b.diff as number)),
    ...correct.filter(row => row.diff === null).sort((a, b) => a.nickname.localeCompare(b.nickname))
  ]);

  return {
    settled: reached !== null,
    finalWeight,
    finalDate: final ? final.measurement_date : null,
    reached,
    reachCamp: bets.filter(row => row.will_reach),
    missCamp: bets.filter(row => !row.will_reach),
    ranking
  };
}
